import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Typography, Button, Modal, Box } from '@mui/material';
import 'bootstrap-icons/font/bootstrap-icons.css';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';

const emptyForm = {
  id: '',
  outcome_name: '',
  status: '1',
};

const CallOutcomeMaster = () => {
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const userInfo = useSelector((state) => state.userInfo);
  const userId = userInfo?.id;

  // ---- get call outcome master list ----
  const getOutcomes = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(`${config.API_BASE_URL}get_lead_call_outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData({
          length: 100,
          start: 0,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOutcomes(Array.isArray(result.data?.data) ? result.data.data : []);
        setError('');
      } else {
        setOutcomes([]);
        setError(result.message || 'No records found.');
      }
    } catch (e) {
      setOutcomes([]);
      setError('Something went wrong while fetching call outcomes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getOutcomes();
  }, [getOutcomes]);

  const openAddModal = () => {
    setForm(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      outcome_name: row.outcome_name || '',
      status: String(row.status ?? '1'),
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---- add / update call outcome ----
  const handleSave = async () => {
    if (!form.outcome_name || !form.outcome_name.trim()) {
      setFormError('Outcome name is required.');
      return;
    }

    try {
      setSaving(true);
      setFormError('');

      const payload = {
        ...form,
        user_id: userId,
      };

      const response = await fetch(`${config.API_BASE_URL}masters/cu_lead_call_outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(payload),
      });

      const result = await response.json();

      if (result.success) {
        closeModal();
        getOutcomes();
      } else {
        setFormError(result.message || 'Could not save the call outcome.');
      }
    } catch (e) {
      setFormError('Something went wrong while saving.');
    } finally {
      setSaving(false);
    }
  };

  // ---- delete ----
  const openDeleteModal = (row) => {
    setDeleteId(row.id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setDeleting(true);

      const response = await fetch(`${config.API_BASE_URL}masters/delete_lead_call_outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData({
          id: deleteId,
          user_id: userId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        closeDeleteModal();
        getOutcomes();
      } else {
        setError(result.message || 'Could not delete the call outcome.');
        closeDeleteModal();
      }
    } catch (e) {
      setError('Something went wrong while deleting.');
      closeDeleteModal();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Container maxWidth="xl">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <Typography variant="h4">Call Outcome Master</Typography>
          <Button variant="contained" onClick={openAddModal}>
            + Add Call Outcome
          </Button>
        </div>

        {error && <div className="text-danger mb-3">{error}</div>}

        <div className="table-wrap">
          <table className="table table-hover table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Outcome Name</th>
                <th>Status</th>
                <th>Created By</th>
                <th style={{ width: 110 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : outcomes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    No call outcomes found.
                  </td>
                </tr>
              ) : (
                outcomes.map((row) => (
                  <tr key={row.id}>
                    <td>{row.formatetdID || row.id}</td>
                    <td>{row.outcome_name || 'N/A'}</td>
                    <td>
                      <span className={row.statusName === 'Active' ? 'badge bg-success' : 'badge bg-danger'}>
                        {row.statusName === 'Active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{row.createdByName || 'N/A'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Edit"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="bi bi-pencil-square" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => openDeleteModal(row)}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* ---- Add / Edit Modal ---- */}
      <Modal open={showModal} onClose={closeModal}>
        <Box
          sx={{
            width: 500,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">{form.id ? 'Edit Call Outcome' : 'Add Call Outcome'}</h4>
            <button type="button" className="btn-close" onClick={closeModal} />
          </div>

          {formError && <div className="text-danger mb-3">{formError}</div>}

          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-bold">Outcome Name</label>
              <input
                type="text"
                name="outcome_name"
                className="form-control"
                value={form.outcome_name}
                onChange={handleChange}
                placeholder="e.g. Connected, Not Reachable"
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-bold">Status</label>
              <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outlined" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* ---- Delete Confirm Modal ---- */}
      <Modal open={showDeleteModal} onClose={closeDeleteModal}>
        <Box
          sx={{
            width: 400,
            maxWidth: '95vw',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <h5 className="mb-3">Delete Call Outcome</h5>
          <p className="mb-4">Are you sure you want to delete this call outcome?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="outlined" onClick={closeDeleteModal} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default CallOutcomeMaster;